import { useMemo, useState } from 'react';
import { Search, UserCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { SlidePanel } from '@/shared/components';
import { useEmployees } from '../../api/employees';
import { useRoleDefinitions } from '../../api/roleDefinitions';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import {
  EMPLOYMENT_TYPES,
  type EmploymentType,
} from '../../types';
import { RoleChip } from './RoleChip';
import { EmployeeDetailPanel } from './EmployeeDetailPanel';
import { ImpersonateUserButton } from '@/shared/components/admin/ImpersonateUserButton';

function formatDate(iso: string | null): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Montreal' });
}

interface EmployeesTabProps {
  onRowClick?: (id: string) => void;
  /** Filter mode driven by the page's tab. */
  scope?: 'active' | 'former' | 'all';
}

export function EmployeesTab({ onRowClick, scope = 'active' }: EmployeesTabProps = {}) {
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const [employmentType, setEmploymentType] = useState<EmploymentType | 'all'>('all');
  const [teamId, setTeamId] = useState<string>('all');
  const [role, setRole] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useEmployees({
    search: debounced || undefined,
    employmentType: employmentType === 'all' ? undefined : [employmentType],
    teamId: teamId === 'all' ? undefined : teamId,
    role: role === 'all' ? undefined : role,
    // 'former' / 'all' scopes need the backend to drop the active-only filter.
    includeFormer: scope === 'former' || scope === 'all',
  });
  const { data: roleDefsData } = useRoleDefinitions();
  const { data: teams = [] } = useTeams();

  const roleDefs = roleDefsData?.roles ?? [];
  const roleDefByKey = useMemo(
    () => new Map(roleDefs.map((r) => [r.role, r])),
    [roleDefs],
  );

  const employees = data?.employees ?? [];

  const typeToneFor = (t: EmploymentType | null) => {
    return EMPLOYMENT_TYPES.find((entry) => entry.value === t)?.tone ?? 'bg-slate-100 text-slate-600';
  };
  const typeLabelFor = (t: EmploymentType | null) => {
    return EMPLOYMENT_TYPES.find((entry) => entry.value === t)?.label ?? '—';
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, position…"
            className="pl-7 w-64 h-8"
          />
        </div>
        <Select value={employmentType} onValueChange={(v) => setEmploymentType(v as EmploymentType | 'all')}>
          <SelectTrigger className="w-44 h-8"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {EMPLOYMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={teamId} onValueChange={setTeamId}>
          <SelectTrigger className="w-44 h-8"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            {teams.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-44 h-8"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {roleDefs.map((r) => (
              <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">
          {employees.length} {employees.length === 1 ? 'person' : 'people'}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Teams</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}><Skeleton className="h-4 w-full" /></TableCell>
                </TableRow>
              ))
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                  No employees match these filters.
                </TableCell>
              </TableRow>
            ) : (
              employees.map((emp) => {
                const displayName = [emp.firstname, emp.lastname].filter(Boolean).join(' ') || emp.email || '—';
                const manager = emp.manager
                  ? [emp.manager.firstname, emp.manager.lastname].filter(Boolean).join(' ') || emp.manager.email
                  : null;
                return (
                  <TableRow
                    key={emp.id}
                    className="cursor-pointer"
                    onClick={() => onRowClick ? onRowClick(emp.id) : setEditingId(emp.id)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <OwnerAvatar
                          ownerId={emp.id}
                          photoUrl={emp.profile_photo_url}
                          name={displayName || emp.email || '?'}
                          className="h-7 w-7"
                          fallbackClassName="bg-primary/10 text-primary text-[10px] font-semibold"
                        />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{displayName}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{emp.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{emp.position || '—'}</TableCell>
                    <TableCell>
                      <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium', typeToneFor(emp.employment_type))}>
                        {typeLabelFor(emp.employment_type)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {emp.teams.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {emp.teams.map((t) => (
                            <span
                              key={t.id}
                              className={cn(
                                'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px]',
                                t.role === 'leader'
                                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                                  : 'bg-muted border-transparent text-muted-foreground',
                              )}
                            >
                              {t.name}
                              {t.role === 'leader' && ' ★'}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {emp.roles.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {emp.roles.map((r) => (
                            <RoleChip key={r.role} role={r.role} definition={roleDefByKey.get(r.role)} />
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {manager ? (
                        <span className="flex items-center gap-1">
                          <UserCircle2 className="h-3 w-3 text-muted-foreground" />
                          {manager}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(emp.login_at)}
                    </TableCell>
                    <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                      {emp.id && (
                        <ImpersonateUserButton
                          targetUserId={emp.id}
                          targetLabel={displayName}
                          iconOnly
                          size="icon"
                          variant="ghost"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <SlidePanel open={!!editingId} storageKey="atek_employees_panel_width">
        <EmployeeDetailPanel
          employeeId={editingId}
          open={!!editingId}
          onOpenChange={(o) => { if (!o) setEditingId(null); }}
        />
      </SlidePanel>
    </div>
  );
}
